import { CAT_RUNNING_ASSETS } from '../assets'
import { CAT_RUNNING_CHARACTER_KEY, CAT_RUNNING_STAGE_KEYS } from '../constants'

export const createImageRecord = (src) => ({
  src,
  image: null,
  isLoaded: false,
})

export const preloadImageRecord = (record) =>
  new Promise((resolve) => {
    if (!record?.src) {
      resolve(record)
      return
    }

    const image = new Image()
    let isResolved = false
    const finalize = () => {
      if (isResolved) return
      isResolved = true
      record.image = image
      record.isLoaded = true
      resolve(record)
    }

    image.onload = () => {
      if (typeof image.decode === 'function') {
        image
          .decode()
          .catch(() => null)
          .finally(finalize)
        return
      }

      finalize()
    }

    image.onerror = () => resolve(record)
    image.src = record.src

    if (image.complete) {
      finalize()
    }
  })

export const createStageAssetRecords = (assets, stageKey) => ({
  background: createImageRecord(assets.backgrounds[stageKey]),
  groundObstacles: (assets.obstacles[stageKey] ?? []).map((src) => createImageRecord(src)),
})

export const createAssetRecords = (assets, characterKey) => {
  const stages = CAT_RUNNING_STAGE_KEYS.reduce((acc, stageKey) => {
    acc[stageKey] = createStageAssetRecords(assets, stageKey)
    return acc
  }, {})

  return {
    stages,
    characterRun: assets.characters[characterKey].run.map((src) => createImageRecord(src)),
    characterDeath: createImageRecord(assets.characters[characterKey].death),
    crowFrames: assets.obstacles.common.crow.map((src) => createImageRecord(src)),
    churu: createImageRecord(assets.items.churu),
    shieldAura: createImageRecord(assets.items.shieldAura),
    gameOver: createImageRecord(assets.ui.gameOver),
  }
}

export const getStageAssets = (assetRecords, stageKey) =>
  assetRecords?.stages?.[stageKey] ?? assetRecords?.stages?.[CAT_RUNNING_STAGE_KEYS[0]] ?? null

export const preloadGameAssetRecords = async (assetRecords) => {
  const stageRecords = CAT_RUNNING_STAGE_KEYS.flatMap((stageKey) => {
    const stage = assetRecords.stages[stageKey]
    return [stage.background, ...stage.groundObstacles]
  })

  const records = await Promise.all([
    ...stageRecords.map((record) => preloadImageRecord(record)),
    ...assetRecords.characterRun.map((record) => preloadImageRecord(record)),
    preloadImageRecord(assetRecords.characterDeath),
    ...assetRecords.crowFrames.map((record) => preloadImageRecord(record)),
    preloadImageRecord(assetRecords.churu),
    preloadImageRecord(assetRecords.shieldAura),
    preloadImageRecord(assetRecords.gameOver),
  ])

  const stageRecordCount = stageRecords.length
  const runStart = stageRecordCount
  const runEnd = runStart + assetRecords.characterRun.length
  const deathIndex = runEnd
  const crowStart = deathIndex + 1
  const crowEnd = crowStart + assetRecords.crowFrames.length
  const churuIndex = crowEnd
  const gameOverIndex = records.length - 1

  const stageLoaded = CAT_RUNNING_STAGE_KEYS.every((stageKey) => {
    const stage = assetRecords.stages[stageKey]
    return stage.background.isLoaded && stage.groundObstacles.every((frame) => frame.isLoaded)
  })
  const runFrames = records.slice(runStart, runEnd)
  const deathFrame = records[deathIndex]
  const crowFrames = records.slice(crowStart, crowEnd)
  const churuRecord = records[churuIndex]
  const gameOverFrame = records[gameOverIndex]

  return {
    backgroundReady: stageLoaded,
    characterReady: runFrames.every((frame) => frame.isLoaded) && deathFrame?.isLoaded === true,
    obstacleReady: stageLoaded && crowFrames.every((frame) => frame.isLoaded),
    itemReady: churuRecord?.isLoaded === true && assetRecords.shieldAura?.isLoaded === true,
    uiReady: gameOverFrame?.isLoaded === true,
  }
}

let _sharedRecords = null
let _sharedPreloadPromise = null

export const getSharedCatRunningAssets = () => {
  if (!_sharedRecords) {
    _sharedRecords = createAssetRecords(CAT_RUNNING_ASSETS, CAT_RUNNING_CHARACTER_KEY)
  }
  return _sharedRecords
}

export const preloadSharedCatRunningAssets = () => {
  if (!_sharedPreloadPromise) {
    _sharedPreloadPromise = preloadGameAssetRecords(getSharedCatRunningAssets())
  }
  return _sharedPreloadPromise
}
